"use client";

import { Minus, Plus } from "lucide-react";
import { useEffect, useState } from "react";

import { MAX_ORIGIN_COUNT } from "../../_logic/constants";

interface CountStepperProps {
  value: number;
  /** Names the count for screen readers, e.g. "People from San Diego". */
  label: string;
  onChange: (count: number) => void;
}

const BUTTON =
  "relative after:absolute after:-inset-0.5 after:content-[''] flex size-10 shrink-0 items-center justify-center rounded-xl text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:text-gray-300 disabled:hover:bg-transparent";

/**
 * A head count you can type or step: − [count] +, at least 1. A typed count is saved as it is
 * typed, so it is never lost when the popover closes.
 * @param props - The count, its label and the change handler.
 * @returns The stepper.
 * @throws Never.
 */
export const CountStepper = ({ value, label, onChange }: CountStepperProps) => {
  // States
  const [text, setText] = useState(String(value));

  // Handlers
  const commit = (next: number) => {
    const clamped = Math.min(Math.max(1, Math.round(next)), MAX_ORIGIN_COUNT);
    setText(String(clamped));
    if (clamped !== value) onChange(clamped);
  };

  // Effects
  useEffect(() => setText(String(value)), [value]);

  // Render
  return (
    <div
      className="flex h-10 shrink-0 items-center rounded-xl border border-gray-200 bg-white"
      role="group"
      aria-label={label}
    >
      {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
      <button
        type="button"
        aria-label={`Fewer: ${label}`}
        disabled={value <= 1}
        onClick={() => commit(value - 1)}
        className={BUTTON}
      >
        <Minus className="size-3.5" />
      </button>
      <input
        aria-label={label}
        inputMode="numeric"
        autoComplete="off"
        data-1p-ignore=""
        data-lpignore="true"
        value={text}
        onChange={(event) => {
          // Whole numbers only: "1.5", "1e5", "-7" or letters are refused, not read as digits.
          const raw = event.target.value.replace(/,/g, "");
          if (!/^\d*$/.test(raw)) return;
          const digits = raw;
          setText(digits);
          // Saved as it is typed, so leaving the popover by clicking elsewhere (which unmounts it
          // before a blur would fire) never loses the count.
          const typed = Number(digits);
          if (digits && typed >= 1 && typed !== value) onChange(Math.min(typed, MAX_ORIGIN_COUNT));
        }}
        onBlur={() => commit(Number(text) || value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit(Number(text) || value);
          if (event.key === "ArrowUp") commit(value + 1);
          if (event.key === "ArrowDown") commit(value - 1);
        }}
        className="h-full w-12 bg-transparent text-center text-base font-medium tabular-nums text-gray-900 focus:outline-none md:text-sm"
      />
      {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
      <button type="button" aria-label={`More: ${label}`} onClick={() => commit(value + 1)} className={BUTTON}>
        <Plus className="size-3.5" />
      </button>
    </div>
  );
};
